import { isProduction } from './app-mode.js';
import { isLedgerNetwork, type LedgerNetwork } from './public-ledger-network.js';

// Reads from the public ledger's mirror node, straight from the browser (#24 phase 2b): what the browser checks a schedule
// against before signing, independent of nodeStream. The mirror is free and public, and answers any origin.

const MIRROR_BASE_URLS: Record<LedgerNetwork, string> = {
  TESTNET: 'https://testnet.mirrornode.hedera.com',
  MAINNET: 'https://mainnet-public.mirrornode.hedera.com',
};

// The mirror lags the ledger by a few seconds, so a schedule made a moment ago may not be there yet: three tries over
// about ten seconds (DECISION 2.2), each given up after a few seconds
export const MIRROR_RETRY_DELAYS_MS = [0, 3_000, 6_000] as const;
export const MIRROR_TIMEOUT_MS = 4_000;

const ENTITY_ID = /^\d+\.\d+\.\d+$/;

/**
 * The network whose mirror the check reads. A production build is on the live network whatever the company record says:
 * a signature names no network, so a check read from another network's mirror would prove nothing. Elsewhere it is the
 * company's network; null when that isn't one.
 */
export function mirrorNetwork(appMode: string | null | undefined, companyNetwork: unknown): LedgerNetwork | null {
  if (isProduction(appMode)) return 'MAINNET';
  return isLedgerNetwork(companyNetwork) ? companyNetwork : null;
}

export function mirrorScheduleUrl(network: LedgerNetwork, scheduleId: string): string | null {
  if (!ENTITY_ID.test(scheduleId)) return null;
  return `${MIRROR_BASE_URLS[network]}/api/v1/schedules/${scheduleId}`;
}

export interface MirrorFetchOptions {
  fetch: (
    url: string,
    init: { signal: AbortSignal; headers: Record<string, string> },
  ) => Promise<{
    ok: boolean;
    json(): Promise<unknown>;
  }>;
  sleep: (ms: number) => Promise<void>;
  delaysMs?: readonly number[];
  timeoutMs?: number;
}

/**
 * The mirror's record of a schedule, or null when the mirror didn't give one after every try (down, lagging, an error, or
 * not JSON). Never throws.
 */
export async function fetchMirrorSchedule(
  network: LedgerNetwork,
  scheduleId: string,
  options: MirrorFetchOptions,
): Promise<unknown> {
  const url = mirrorScheduleUrl(network, scheduleId);
  if (!url) return null;
  for (const delay of options.delaysMs ?? MIRROR_RETRY_DELAYS_MS) {
    if (delay > 0) await options.sleep(delay);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? MIRROR_TIMEOUT_MS);
    try {
      const res = await options.fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (res.ok) {
        const record = await res.json();
        if (typeof record === 'object' && record !== null) return record;
      }
    } catch {
      // Tried again below
    } finally {
      clearTimeout(timer);
    }
  }
  return null;
}

// --- The re-sign check (#25 phase 4) ---

// What the browser reads for itself from the public ledger's mirror before signing a re-signed automatic payments allowance
// (#25 phase 4, "Operating account anchor for a re-sign", settled 2026-10-26). nodeStream's servers prepare what is signed,
// so the browser confirms two things from the public record they can't fake:
// - which account is the company's operating account: the account its earlier top-ups were paid by and went to (a transfer
//   out of the company account, paid by an account that received it), the spender of an allowance the company granted, or
//   the spender of an executed approval of the company's (only the company's own rule can have run one);
// - that the schedule being signed holds exactly one allowance, of the ledger's maximum, from the company account to that
//   operating account.
// The reads themselves are in public-ledger-mirror-api.ts.

// The mirrors the browser reads (the same hosts as ns-lambda-ts _core/public-ledger/mirror.ts); both answer CORS for any
// origin (checked 2026-10-26)
export const MIRROR_HOSTS: Record<LedgerNetwork, string> = MIRROR_BASE_URLS;

// The ledger's maximum allowance (Long.MAX, #25 DECISION 1.3), compared as a string so no precision is lost
export const MAX_ALLOWANCE_TINYBARS = '9223372036854775807';

// An account id "shard.realm.num" as the mirror writes it
const ACCOUNT_ID = /^\d+\.\d+\.\d+$/;

// A transaction as the mirror's /api/v1/transactions lists it (only what is read here)
export interface MirrorTransaction {
  transaction_id?: string | null;
  name?: string | null;
  result?: string | null;
  transfers?: { account?: string | null; amount?: number | string | null }[] | null;
}

// An allowance as /api/v1/accounts/{id}/allowances/crypto lists it
export interface MirrorAllowance {
  owner?: string | null;
  spender?: string | null;
}

// A schedule as /api/v1/schedules/{id} answers it
export interface MirrorSchedule {
  schedule_id?: string | null;
  deleted?: boolean | null;
  executed_timestamp?: string | null;
  // base64 of the SchedulableTransactionBody
  transaction_body?: string | null;
}

// The account that paid a transaction, from the mirror's transaction id "0.0.123-1700000000-000000000"
export function payerOf(transactionId: string | null | undefined): string | null {
  if (typeof transactionId !== 'string') return null;
  const [payer] = transactionId.split(/[-@]/);
  return ACCOUNT_ID.test(payer) ? payer : null;
}

function amountOf(value: number | string | null | undefined): number | null {
  const amount = typeof value === 'string' ? Number(value) : value;
  return typeof amount === 'number' && Number.isFinite(amount) ? amount : null;
}

// No account in the company's public ledger history: a company setting up automatic payments before it ever topped up
export const NO_HISTORY_REFUSAL =
  "Your company's operating account can't be found in its public ledger history, so this approval can't be checked. Please contact nodeStream support.";

export type OperatingAnchor = { accountId: string; refusal: null } | { accountId: null; refusal: string };

function base64Bytes(value: string | null | undefined): Uint8Array | null {
  if (!value) return null;
  try {
    return Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
}

/**
 * The spender of an approval the company's own rule ran: a schedule the mirror shows executed and not deleted, holding
 * exactly one hbar allowance of the ledger's maximum from the company account and nothing else. Whoever named the
 * schedule, only the company's signatories can have made it run, so its spender is one the company approved. Null for
 * anything else.
 */
export function executedApprovalSpender(
  schedule: MirrorSchedule | null | undefined,
  companyAccountId: string,
): string | null {
  if (!schedule || schedule.deleted || !schedule.executed_timestamp) return null;
  const bytes = base64Bytes(schedule.transaction_body);
  const approve = bytes ? readScheduledApprove(bytes) : null;
  if (!approve?.isApprove || approve.otherAllowances > 0 || approve.hbarAllowances.length !== 1) return null;
  const [allowance] = approve.hbarAllowances;
  if (allowance.owner !== companyAccountId || allowance.amount !== MAX_ALLOWANCE_TINYBARS) return null;
  return allowance.spender && ACCOUNT_ID.test(allowance.spender) ? allowance.spender : null;
}

/**
 * The company's operating account as the public ledger's own history shows it, or why it can't be told:
 * - the payer of a successful transfer that took credits out of the company account and paid them to that same payer (a
 *   top-up: only the company's signatories, or the spender the company approved, can move credits out of it);
 * - the spender of an allowance the company account granted;
 * - the spender of the company's last executed approval (executedApprovalSpender), which is still there when the allowance
 *   itself has gone, the very case a re-sign is for.
 * Every account found must be the same one: none, or two different ones, is a refusal, never a guess.
 */
export function operatingAccountFromHistory(input: {
  companyAccountId: string | null | undefined;
  transactions: readonly MirrorTransaction[];
  allowances: readonly MirrorAllowance[];
  approvals?: readonly (MirrorSchedule | null)[];
}): OperatingAnchor {
  const company = input.companyAccountId;
  if (!company || !ACCOUNT_ID.test(company)) {
    return { accountId: null, refusal: "Your company's public ledger account is not known, so this can't be checked." };
  }
  const found = new Set<string>();
  for (const approval of input.approvals ?? []) {
    const spender = executedApprovalSpender(approval, company);
    if (spender) found.add(spender);
  }
  for (const allowance of input.allowances) {
    if (allowance.owner === company && allowance.spender && ACCOUNT_ID.test(allowance.spender)) {
      found.add(allowance.spender);
    }
  }
  for (const transaction of input.transactions) {
    if (transaction.result !== 'SUCCESS') continue;
    const payer = payerOf(transaction.transaction_id);
    if (!payer || payer === company) continue;
    const transfers = transaction.transfers ?? [];
    const net = (account: string) =>
      transfers.filter((t) => t.account === account).reduce((sum, t) => sum + (amountOf(t.amount) ?? 0), 0);
    if (net(company) < 0 && net(payer) > 0) found.add(payer);
  }
  if (found.size === 0) return { accountId: null, refusal: NO_HISTORY_REFUSAL };
  if (found.size > 1) {
    return {
      accountId: null,
      refusal:
        "Your company's public ledger history names more than one account drawing on it, so this approval was not signed. Please contact nodeStream support.",
    };
  }
  const [accountId] = found;
  return { accountId, refusal: null };
}

// --- A minimal protobuf reader, for the one message read here ---

// BigInt constants (the build targets ES2018, which has no bigint literals)
const B0 = BigInt(0);
const B3 = BigInt(3);
const B7 = BigInt(7);
const B70 = BigInt(70);
const BYTE = BigInt(0x7f);
const U64 = BigInt('0xffffffffffffffff');
const I64_MIN = BigInt('0x8000000000000000');
const I64_SPAN = BigInt('0x10000000000000000');

class ProtoReader {
  private __at = 0;

  constructor(private readonly __bytes: Uint8Array) {}

  get done() {
    return this.__at >= this.__bytes.length;
  }

  varint(): bigint {
    let result = B0;
    for (let shift = B0; shift < B70; shift += B7) {
      if (this.__at >= this.__bytes.length) throw new Error('Truncated varint');
      const byte = this.__bytes[this.__at++];
      result |= (BigInt(byte) & BYTE) << shift;
      if ((byte & 0x80) === 0) return result;
    }
    throw new Error('Varint too long');
  }

  bytes(): Uint8Array {
    const length = Number(this.varint());
    if (length < 0 || this.__at + length > this.__bytes.length) throw new Error('Truncated field');
    const out = this.__bytes.subarray(this.__at, this.__at + length);
    this.__at += length;
    return out;
  }

  // The next field: its number, and its value (a varint as bigint, a length-delimited field as bytes)
  field(): { number: number; value: bigint | Uint8Array } {
    const tag = this.varint();
    const number = Number(tag >> B3);
    const wireType = Number(tag & B7);
    if (wireType === 0) return { number, value: this.varint() };
    if (wireType === 2) return { number, value: this.bytes() };
    if (wireType === 1 || wireType === 5) {
      const size = wireType === 1 ? 8 : 4;
      if (this.__at + size > this.__bytes.length) throw new Error('Truncated field');
      this.__at += size;
      return { number, value: B0 };
    }
    throw new Error(`Unsupported wire type ${wireType}`);
  }
}

// Every field of a message, in order
function fieldsOf(bytes: Uint8Array): { number: number; value: bigint | Uint8Array }[] {
  const reader = new ProtoReader(bytes);
  const fields = [];
  while (!reader.done) fields.push(reader.field());
  return fields;
}

// An int64 varint read as signed
function asInt64(value: bigint): bigint {
  const masked = value & U64;
  return masked >= I64_MIN ? masked - I64_SPAN : masked;
}

// AccountID {shardNum 1, realmNum 2, accountNum 3, alias 4}: "shard.realm.num", or null for an alias or anything else
function accountIdOf(bytes: Uint8Array): string | null {
  const parts: (bigint | null)[] = [B0, B0, null];
  for (const { number, value } of fieldsOf(bytes)) {
    if (typeof value !== 'bigint' || number < 1 || number > 3) return null;
    parts[number - 1] = asInt64(value);
  }
  if (parts[2] === null || parts.some((p) => p !== null && p < B0)) return null;
  return parts.map(String).join('.');
}

export interface HbarAllowance {
  owner: string | null;
  spender: string | null;
  // tinybars, as a decimal string
  amount: string;
}

export interface ScheduledApprove {
  // Whether the scheduled transaction is a CryptoApproveAllowance and nothing else
  isApprove: boolean;
  hbarAllowances: HbarAllowance[];
  // Token and NFT allowances in the same approval
  otherAllowances: number;
}

// SchedulableTransactionBody: transactionFee 1, memo 2, cryptoApproveAllowance 37; any other field is another kind of
// transaction (or one this reader doesn't know), so it is never read as an approval
const SCHEDULABLE_FEE = 1;
const SCHEDULABLE_MEMO = 2;
const SCHEDULABLE_APPROVE = 37;

/**
 * Reads the scheduled transaction the mirror holds for a schedule (`transaction_body`, a SchedulableTransactionBody), as
 * far as an allowance approval goes. Null when the bytes can't be read at all.
 */
export function readScheduledApprove(body: Uint8Array): ScheduledApprove | null {
  try {
    const empty: ScheduledApprove = { isApprove: false, hbarAllowances: [], otherAllowances: 0 };
    const fields = fieldsOf(body);
    const approves = fields.filter((f) => f.number === SCHEDULABLE_APPROVE);
    const others = fields.filter((f) => ![SCHEDULABLE_FEE, SCHEDULABLE_MEMO, SCHEDULABLE_APPROVE].includes(f.number));
    if (approves.length !== 1 || others.length > 0 || !(approves[0].value instanceof Uint8Array)) return empty;

    const result: ScheduledApprove = { isApprove: true, hbarAllowances: [], otherAllowances: 0 };
    // CryptoApproveAllowanceTransactionBody: cryptoAllowances 1, nftAllowances 2, tokenAllowances 3
    for (const { number, value } of fieldsOf(approves[0].value)) {
      if (number !== 1) {
        result.otherAllowances += 1;
        continue;
      }
      if (!(value instanceof Uint8Array)) return null;
      // CryptoAllowance: owner 1, spender 2, amount 3
      const allowance: HbarAllowance = { owner: null, spender: null, amount: '0' };
      for (const part of fieldsOf(value)) {
        if (part.number === 1 && part.value instanceof Uint8Array) allowance.owner = accountIdOf(part.value);
        else if (part.number === 2 && part.value instanceof Uint8Array) allowance.spender = accountIdOf(part.value);
        else if (part.number === 3 && typeof part.value === 'bigint') allowance.amount = asInt64(part.value).toString();
        else return null;
      }
      result.hbarAllowances.push(allowance);
    }
    return result;
  } catch {
    return null;
  }
}
